/**
 * JourneyDisplay.tsx
 *
 * Unified journey display component that always renders the editable experience.
 * Editing is enabled by default.
 */

import React, { useEffect } from 'react';
import { JourneyType } from '../types/journey';
import EditableJourney from './EditableJourney';
import { JourneyEditProvider, useJourneyEdit } from '../contexts/JourneyEditContext';

/**
 * Props for JourneyDisplay component.
 * 
 * This component provides a unified interface for displaying journeys with
 * editing enabled by default (all journeys are editable). The editing feature
 * automatically handles both saved and unsaved journeys with consistent behavior.
 */
interface JourneyDisplayProps {
  journey: JourneyType;
  onSaveJourney?: () => void;
  onModeChange?: (mode: 'view' | 'edit') => void;
  onJourneySaved?: (saved: any) => void;
  onEditStateChange?: (s: { mode: 'view' | 'edit'; awaitingApproval: boolean }) => void;
  onRegisterDiscard?: (discardFn: () => void) => void;
}

/**
 * JourneyDisplay - Unified component for displaying journeys
 * 
 * This component always renders the editable journey experience (with the edit
 * context). The `enableEdit` prop is preserved for compatibility but is no longer
 * used to switch to a read-only component.
 * 
 * @param journey - The journey data to display
 * @param mode - Display mode: 'view' (read-only) or 'edit' (editable)
 * @param onSaveJourney - Callback for saving journey to user's collection
 * @param onJourneySaved - Callback when journey is saved/updated
 */
const JourneyDisplay: React.FC<JourneyDisplayProps> = ({
  journey,
  onSaveJourney,
  onModeChange,
  onJourneySaved,
  onEditStateChange,
  onRegisterDiscard
}) => {
  return (
    <JourneyEditProvider>
      <EditableJourneyWrapper 
        journey={journey}
        onSaveJourney={onSaveJourney}
        onJourneySaved={onJourneySaved}
        onEditStateChange={onEditStateChange}
        onRegisterDiscard={onRegisterDiscard}
      />
    </JourneyEditProvider>
  );
};

/**
 * Internal wrapper component that uses the journey edit context
 */
interface EditableJourneyWrapperProps {
  journey: JourneyType;
  onSaveJourney?: () => void;
  onModeChange?: (mode: 'view' | 'edit') => void;
  onJourneySaved?: (saved: JourneyType) => void;
  onEditStateChange?: (s: { mode: 'view' | 'edit'; awaitingApproval: boolean }) => void;
  onRegisterDiscard?: (discardFn: () => void) => void;
}

const EditableJourneyWrapper: React.FC<EditableJourneyWrapperProps> = ({
  journey,
  onSaveJourney,
  onModeChange,
  onJourneySaved,
  onEditStateChange,
  onRegisterDiscard
}) => {
  return (
    <EditableJourneyModeTracker 
      journey={journey}
      onSaveJourney={onSaveJourney}
      onModeChange={onModeChange}
      onJourneySaved={onJourneySaved}
      onEditStateChange={onEditStateChange}
      onRegisterDiscard={onRegisterDiscard}
    />
  );
};

/**
 * Component that tracks edit mode changes and notifies parent
 */
/**
 * Component that tracks edit mode changes and notifies parent
 */
const EditableJourneyModeTracker: React.FC<EditableJourneyWrapperProps> = ({
  journey,
  onSaveJourney,
  onModeChange,
  onJourneySaved,
  onEditStateChange,
  onRegisterDiscard
}) => {
  const { state } = useJourneyEdit();

  useEffect(() => {
    if (onModeChange) {
      onModeChange(state.mode);
    }
  }, [state.mode, onModeChange]);

  useEffect(() => {
    if (onEditStateChange) {
      onEditStateChange({ mode: state.mode, awaitingApproval: state.awaitingApproval });
    }
  }, [state.mode, state.awaitingApproval, onEditStateChange]);

  return (
    <EditableJourney 
      journey={journey}
      onSaveJourney={onSaveJourney}
      onJourneySaved={onJourneySaved}
      registerExternalDiscard={onRegisterDiscard}
    />
  );
};

export default JourneyDisplay;
